/**
 * Contract Form Context
 * Provides shared state for the contract wizard that exists outside react-hook-form
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const ContractFormContext = createContext(null);

export function ContractFormProvider({
  children,
  mode = 'create',
  contractId = null,
  initialData = null
}) {
  const isEdit = mode === 'edit';
  const isEditable = !initialData?.status || initialData?.status === 'Draft';

  // Reference data state
  const [refData, setRefData] = useState({
    investors: [],
    products: [],
    frequencies: [],
    interestTypes: [],
    interestRates: [],
    placementFees: [],
    brokers: [],
    collateralProviders: [],
    issuers: [],
    collateralTypes: [],
    currencies: [],
    calculationProfiles: [],
    commissionRates: [],
  });
  const [refDataLoading, setRefDataLoading] = useState(true);
  const [refDataError, setRefDataError] = useState(null);

  // Multi-investor state (not part of react-hook-form)
  const [contractInvestors, setContractInvestors] = useState([]);
  // Snapshot of contract investors as loaded from DB — used to detect changes on save
  const [originalContractInvestors, setOriginalContractInvestors] = useState([]);

  // Multi-collateral state (not part of react-hook-form). Rows are kept in
  // an array of objects matching the contract_collaterals shape — id is set
  // for rows that already exist server-side, omitted for pending new rows.
  const [contractCollaterals, setContractCollaterals] = useState([]);
  const [originalContractCollaterals, setOriginalContractCollaterals] = useState([]);

  // Ad-hoc capital events state
  const [adhocCapitalEvents, setAdhocCapitalEvents] = useState([]);
  // Snapshot of adhoc capital events as loaded from DB — used to detect deletions on save
  const [originalAdhocCapitalEvents, setOriginalAdhocCapitalEvents] = useState([]);

  // Ad-hoc interest payment dates state
  const [adhocInterestPayments, setAdhocInterestPayments] = useState([]);
  // Snapshot of adhoc interest payments as loaded from DB
  const [originalAdhocInterestPayments, setOriginalAdhocInterestPayments] = useState([]);

  // Schedule state (edit mode)
  const [hasSchedule, setHasSchedule] = useState(false);
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [generatingSchedule, setGeneratingSchedule] = useState(false);

  // Summary step data (Step 5)
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  // Whether the chosen Product Type requires a program (and whether that's
  // still resolving). Published by ContractDetailsStep from its program fetch
  // and read by the step validator to gate the Next button.
  const [programRequirement, setProgramRequirement] = useState({ required: false, loading: false });

  // Benchmark rate selection (for UI display)
  const [selectedBenchmarkRate, setSelectedBenchmarkRate] = useState('');

  // Show Step 5 (Contract Summary) in edit mode for all contracts
  const showSummaryStep = isEdit && initialData;

  // Step completion tracking - in edit mode, all steps are considered completed
  // If beyond draft, also include step 5 (the summary step)
  const [completedSteps, setCompletedSteps] = useState(() => {
    if (!isEdit) return [];
    if (showSummaryStep) return [0, 1, 2, 3, 4, 5];
    return [0, 1, 2, 3, 4];
  });

  // Mark a step as completed
  const markStepCompleted = useCallback((stepIndex) => {
    setCompletedSteps(prev => {
      if (prev.includes(stepIndex)) return prev;
      return [...prev, stepIndex].sort((a, b) => a - b);
    });
  }, []);

  // Multi-investor handlers
  const handleAddInvestor = useCallback((investor) => {
    if (contractInvestors.some(i => i.investor_id === investor.investor_id)) {
      return false; // Already exists
    }
    setContractInvestors(prev => [...prev, investor]);
    return true;
  }, [contractInvestors]);

  const handleUpdateInvestor = useCallback((investorId, capitalAmount, newFundsAmount, rolledFundsAmount, likelihoodToRoll) => {
    setContractInvestors(prev =>
      prev.map(inv =>
        inv.investor_id === investorId
          ? {
              ...inv,
              capital_amount: capitalAmount,
              new_funds_amount: newFundsAmount !== undefined ? newFundsAmount : inv.new_funds_amount,
              rolled_funds_amount: rolledFundsAmount !== undefined ? rolledFundsAmount : inv.rolled_funds_amount,
              likelihood_to_roll: likelihoodToRoll !== undefined ? likelihoodToRoll : inv.likelihood_to_roll,
            }
          : inv
      )
    );
  }, []);

  const handleRemoveInvestor = useCallback((investorId) => {
    setContractInvestors(prev => prev.filter(inv => inv.investor_id !== investorId));
  }, []);

  // Ad-hoc capital event handlers. eventType defaults to 'withdrawal' so existing
  // callers (single-section UI) keep working; the split UI passes 'interest_accrual'
  // when adding from the Adhoc Interest Accruals section.
  const handleAddAdhocCapitalEvent = useCallback((eventType = 'withdrawal') => {
    setAdhocCapitalEvents(prev => [
      ...prev,
      {
        id: Date.now(), // Temporary ID for UI tracking
        event_date: '',
        event_type: eventType,
        amount: '',
        description: '',
        isNew: true, // Track new vs existing for edit mode
      },
    ]);
  }, []);

  const handleUpdateAdhocCapitalEvent = useCallback((index, field, value) => {
    setAdhocCapitalEvents(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleRemoveAdhocCapitalEvent = useCallback((index) => {
    setAdhocCapitalEvents(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Ad-hoc interest payment handlers
  const handleAddAdhocInterestPayment = useCallback(() => {
    setAdhocInterestPayments(prev => [
      ...prev,
      {
        id: Date.now(),
        event_date: '',
        description: '',
        isNew: true,
      },
    ]);
  }, []);

  const handleUpdateAdhocInterestPayment = useCallback((index, field, value) => {
    setAdhocInterestPayments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleRemoveAdhocInterestPayment = useCallback((index) => {
    setAdhocInterestPayments(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Append a newly created stakeholder to the appropriate refData array
  const appendToRefData = useCallback((key, newItem) => {
    setRefData(prev => ({
      ...prev,
      [key]: [...prev[key], newItem],
    }));
  }, []);

  // Clear validation messages
  const clearValidation = useCallback(() => {
    setValidationErrors([]);
    setValidationWarnings([]);
  }, []);

  const value = {
    // Mode
    mode,
    isEdit,
    contractId,
    initialData,
    showSummaryStep,

    // Reference data
    refData,
    setRefData,
    appendToRefData,
    refDataLoading,
    setRefDataLoading,
    refDataError,
    setRefDataError,

    // Multi-investor
    contractInvestors,
    setContractInvestors,
    originalContractInvestors,
    setOriginalContractInvestors,
    handleAddInvestor,
    handleUpdateInvestor,
    handleRemoveInvestor,

    // Multi-collateral
    contractCollaterals,
    setContractCollaterals,
    originalContractCollaterals,
    setOriginalContractCollaterals,

    // Ad-hoc capital events
    adhocCapitalEvents,
    setAdhocCapitalEvents,
    originalAdhocCapitalEvents,
    setOriginalAdhocCapitalEvents,
    handleAddAdhocCapitalEvent,
    handleUpdateAdhocCapitalEvent,
    handleRemoveAdhocCapitalEvent,

    // Ad-hoc interest payment dates
    adhocInterestPayments,
    setAdhocInterestPayments,
    originalAdhocInterestPayments,
    setOriginalAdhocInterestPayments,
    handleAddAdhocInterestPayment,
    handleUpdateAdhocInterestPayment,
    handleRemoveAdhocInterestPayment,

    // Schedule
    hasSchedule,
    setHasSchedule,
    schedulePreview,
    setSchedulePreview,
    generatingSchedule,
    setGeneratingSchedule,

    // Summary step data (Step 5)
    summaryData,
    setSummaryData,
    summaryLoading,
    setSummaryLoading,

    // Submission
    submitting,
    setSubmitting,

    // Validation
    validationErrors,
    setValidationErrors,
    validationWarnings,
    setValidationWarnings,
    clearValidation,

    // Program requirement (product-first gate)
    programRequirement,
    setProgramRequirement,

    // Benchmark rate
    selectedBenchmarkRate,
    setSelectedBenchmarkRate,

    // Step completion
    completedSteps,
    setCompletedSteps,
    markStepCompleted,

    // Editability (false for Active/Approved/Matured contracts)
    isEditable,
  };

  return (
    <ContractFormContext.Provider value={value}>
      {children}
    </ContractFormContext.Provider>
  );
}

export function useContractFormContext() {
  const context = useContext(ContractFormContext);
  if (!context) {
    throw new Error('useContractFormContext must be used within a ContractFormProvider');
  }
  return context;
}

export default ContractFormContext;
